import React, {useState} from 'react';
import {Toast} from 'antd-mobile';
import {Trans, useTranslation} from 'react-i18next';
import {t} from '../../../i18n';
import PickUpSlideToStart from '../components/PickUpSlideToStart';
import {PickupWorkStartInfo} from '../graphql/queries/getPickupWorkStartInfo';
import {startPickupWorkNonBlocking} from '../graphql/mutations/startPickupWork';
import {savePendingWorkStart} from '../services/PickupWorkStartRetry';

interface PickupStartWorkPageProps {
    info: PickupWorkStartInfo;
    /** 滑动完成后放行到任务列表（已打点） */
    onDone: () => void;
    /** 「仅查看今日任务」：不打点，以只读态进入任务列表 */
    onPreview: () => void;
    /**
     * 今日无任务时滑动完成：不打点，去看历史作业。
     * 返回 Promise 是为了让滑块在它跑完前保持禁用——它内部还要再拉一次列表，
     * 提前解锁会让司机以为没反应而连滑，触发重复查询与重复跳转
     */
    onViewHistory: () => Promise<void>;
    /** 重新拉取一次启动信息，返回最新值（失败返回 null）。用于滑动那一刻校正陈旧的 info */
    onRefresh: () => Promise<PickupWorkStartInfo | null>;
}

/** 分钟数按原型口径格式化为小时（一位小数，整小时不带小数点）：90 → 1.5、120 → 2 */
const formatHours = (minutes: number): string => {
    const hours = minutes / 60;
    return Number.isInteger(hours) ? String(hours) : hours.toFixed(1);
};

/**
 * 司机端「开始作业」启动页。
 *
 * 三态由后端一次查询返回的 hasTodayTask / pendingTaskId 组合决定（见 getPickupWorkStartInfo）：
 * 有待开始的任务就展示「开始作业」滑块并落库打点，今日无任务则降级为「滑动查看历史作业」、
 * 不产生任何写入。今日任务都已开始的情况根本不会渲染本页。
 *
 * 另有第四条出路：「仅查看今日任务」——司机想先看今天的任务量和路线再决定什么时候开工。
 * 这条路**不打点**，进入的是只读列表（见 PickingUpPage 的 workPreviewMode）。
 * 没有它，司机看一眼任务就被记成已开工，act_start_work_time 失真、需求点 1 的监控目的落空。
 *
 * 两种滑动的**落点不同**：有待开始任务时落在揽收中列表；今日无任务时文案是「滑动查看历史作业」，
 * 就该落在「已完成」页——落回揽收中只会得到一个空列表，司机还得自己去菜单里找已完成。
 * 但「今日无任务」不等于「没活干」（跨天未完成的任务不在今日口径内），
 * 所以最终落哪由 PickingUpPage.handleViewHistory 再确认一次，本页不自行决定。
 */
const PickupStartWorkPage: React.FC<PickupStartWorkPageProps> = ({info, onDone, onPreview, onRefresh, onViewHistory}) => {
    const {i18n} = useTranslation();
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [sliderResetSignal, setSliderResetSignal] = useState<number>(0);
    const hasPendingTask = !!info.pendingTaskId;

    const handleSlideComplete = async () => {
        if (submitting) {
            return;
        }
        setSubmitting(true);
        try {
            const taskId = info.pendingTaskId;
            if (!taskId) {
                // info 是进入页面时查的一次快照，而「本日任务暂未安排」的司机会**长时间停在本页**等派单。
                // 期间调度排了任务，直接按快照放行就等于跳过打点，且 onDone 还会置当天的 dismissed
                // ——这个任务的实际出发时间永久为空，页面上毫无提示。滑动这一刻必须重新核一次。
                const fresh = await onRefresh();
                if (fresh?.pendingTaskId) {
                    // 不放行：文案是「滑动查看历史作业」，司机这一滑的意图不是开工，
                    // 直接替他打点等于替他决定开工时刻。页面会随 info 更新切成「开始作业」态，再滑一次即打点
                    setSliderResetSignal((prev) => prev + 1);
                    Toast.show({content: t('pages.pickUp.startWork.newTaskArrived'), position: 'center'});
                    return;
                }
                if (!fresh) {
                    // 重查失败（弱网）：这一刻对「今天到底有没有任务」的认知最弱，不做不可逆的动作。
                    // 已完成页是 network-only 且不轮询，断网跳过去就是一片空白、也不会自己恢复；
                    // 落回揽收中列表至少还有轮询兜着。拦住司机比漏一次打点更糟，所以仍然放行
                    onDone();
                    return;
                }
                // 确认了今天确实没有待开始的任务，才去看历史
                await onViewHistory();
                return;
            }
            // 时刻在滑动完成这一刻定下来，不能等补传时再取
            const slidAt = new Date().toISOString();
            const recorded = await startPickupWorkNonBlocking(taskId, slidAt);
            if (!recorded) {
                // 打点失败不拦人，但要落盘等有网补传，只提示会静默丢数据
                await savePendingWorkStart(taskId, slidAt);
                Toast.show({content: t('pages.pickUp.startWork.startFailed'), position: 'center'});
            }
            onDone();
        } finally {
            setSubmitting(false);
        }
    };

    // 「9月20日星期日」/「Sunday, September 20」：交给 Intl 按当前语言排布，不自己拼
    const todayText = new Date().toLocaleDateString(i18n.language, {
        month: 'long',
        day: 'numeric',
        weekday: 'long',
    });

    return (
        <div className="flex flex-col h-screen bg-white">
            <div className="flex-1 flex flex-col items-center justify-center px-8 overflow-y-auto">
                <svg width="72" height="72" viewBox="0 0 24 24" fill="none" className="mb-6" style={{color: 'var(--adm-color-primary)'}}>
                    <path d="M1 5h13v9H1zM14 9h5l3 3v2h-8z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
                    <circle cx="6" cy="16" r="2" fill="#fff" stroke="currentColor" strokeWidth="1.3"/>
                    <circle cx="18" cy="16" r="2" fill="#fff" stroke="currentColor" strokeWidth="1.3"/>
                </svg>
                <div className="text-lg font-bold text-center text-[#1A1A1E]">{t('pages.pickUp.startWork.title')}</div>
                <div className="text-sm text-[#8A8A93] mt-2 text-center">{todayText}</div>

                <div className="mt-6 w-full flex flex-col gap-2.5">
                    {/* 昨日回顾：昨日无任务时后端返回 null，整块不渲染（不显示 0 件、0 分钟误导司机） */}
                    {info.yesterdayFinishedAppointmentCount !== null && (
                        <div className="bg-[#FAFAFA] rounded-xl px-4 py-3.5">
                            <div className="text-xs font-semibold text-[#9A9AA5] mb-1.5">
                                {t('pages.pickUp.startWork.yesterdayReview')}
                            </div>
                            <div className="text-[13.5px] text-[#4A4A55]">
                                <Trans
                                    i18nKey="pages.pickUp.startWork.yesterdaySummary"
                                    values={{
                                        addresses: info.yesterdayFinishedAppointmentCount,
                                        hours: info.yesterdayWorkingMinutes !== null ? formatHours(info.yesterdayWorkingMinutes) : '-',
                                    }}
                                    components={{b: <b className="text-[#1A1A1E]"/>}}
                                />
                            </div>
                        </div>
                    )}

                    <div className="rounded-xl px-4 py-3.5" style={{background: '#F1EEFB'}}>
                        <div className="text-xs font-semibold mb-1.5" style={{color: '#7A66D6'}}>
                            {t('pages.pickUp.startWork.todayStatus')}
                        </div>
                        <div className="text-[13.5px]" style={{color: 'var(--adm-color-primary)'}}>
                            {info.hasTodayTask ? (
                                <Trans
                                    i18nKey="pages.pickUp.startWork.todaySummary"
                                    values={{addresses: info.todayAppointmentCount}}
                                    components={{b: <b/>}}
                                />
                            ) : (
                                t('pages.pickUp.startWork.noTaskToday')
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* 只读查看入口：今日无任务时没有可看的任务，不渲染 */}
            {info.hasTodayTask && (
                <div
                    className="text-center text-[13.5px] text-[#8A8A93] underline mb-4"
                    onClick={onPreview}
                >
                    {t('pages.pickUp.startWork.viewTodayTasksOnly')}
                </div>
            )}

            <div className="px-6 pb-10">
                <PickUpSlideToStart
                    text={hasPendingTask
                        ? t('pages.pickUp.startWork.slideToStart')
                        : t('pages.pickUp.startWork.slideToHistory')}
                    disabled={submitting}
                    resetSignal={sliderResetSignal}
                    onComplete={handleSlideComplete}
                />
            </div>
        </div>
    );
};

export default PickupStartWorkPage;
